import {
  buildStoreAvailabilityMap,
  getAvailabilityStatus,
  getStoreQuantityAtLocation,
  isStandardDeliveryEligible,
  isInStoreEligible,
  isPharmacistOnlyProduct,
  isStoreFulfilmentEligible,
  parseGettingYourOrderData,
} from '../../global/store-locator/availability';
import { getDeliveryLocation, getShopLocation } from '../../global/store-locator/storage';

const SHOP_ICON = `<svg class="getting-your-order__icon" width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true"><path d="M2 6.5L3 2.5H13L14 6.5V13.5C14 14.05 13.55 14.5 13 14.5H3C2.45 14.5 2 14.05 2 13.5V6.5Z" stroke="currentColor" stroke-width="1.2"/><path d="M2 6.5H14" stroke="currentColor" stroke-width="1.2"/><path d="M6 6.5V14.5M10 6.5V14.5" stroke="currentColor" stroke-width="1.2"/></svg>`;

const DELIVERY_ICON = `<svg class="getting-your-order__icon" width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true"><path d="M1 11.5H2.2C2.2 12.6 3.1 13.5 4.2 13.5C5.3 13.5 6.2 12.6 6.2 11.5H9.8C9.8 12.6 10.7 13.5 11.8 13.5C12.9 13.5 13.8 12.6 13.8 11.5H15L13.5 7.5H10V5.5H6.5L5 1.5H1V11.5Z" stroke="currentColor" stroke-width="1.2" stroke-linejoin="round"/></svg>`;

const CHEVRON_ICON = `<svg class="getting-your-order__selector-chevron" width="12" height="7" viewBox="0 0 12 7" fill="none" aria-hidden="true"><path d="M5.52344 6.42578L0.273438 1.17578C0 0.929688 0 0.519531 0.273438 0.246094C0.519531 0 0.929688 0 1.20312 0.246094L5.98828 5.05859L10.7734 0.273438C11.0195 0 11.4297 0 11.7031 0.273438C11.9492 0.519531 11.9492 0.929688 11.7031 1.17578L6.42578 6.42578C6.17969 6.69922 5.76953 6.69922 5.52344 6.42578Z" fill="currentColor"/></svg>`;

function formatDetailHtml(text) {
  if (!text) return '';
  return escapeHtml(text)
    .split(/\n|\|/)
    .map((line) => line.trim())
    .filter(Boolean)
    .join('<br>');
}

function buildStandardDeliveryDetailHtml(settings) {
  const promoHtml = settings.standardDeliveryPromo
    ? `<span class="getting-your-order__promo">${escapeHtml(settings.standardDeliveryPromo)}</span>`
    : '';
  const detailHtml = formatDetailHtml(settings.standardDeliveryDetail);

  if (promoHtml && detailHtml) {
    return `${promoHtml}<br>${detailHtml}`;
  }

  return promoHtml || detailHtml;
}

function getBlockSettings(block) {
  return {
    clickCollectTime: block.dataset.clickCollectTime ?? 'Usually ready within 4 hours when ordered before 2pm*',
    standardDeliveryPromo: block.dataset.standardDeliveryPromo ?? 'FREE* for Orders $79+',
    standardDeliveryDetail: block.dataset.standardDeliveryDetail ?? 'Urban Delivery: 2-5 Business Days|Rural Delivery: 3-7 Business Days',
    sameDayDetail: block.dataset.sameDayDetail ?? 'Within 3 hours when ordered before 2pm*',
  };
}

function escapeHtml(value) {
  return String(value)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

function getBlockIcons(block) {
  return {
    available: block.querySelector('.js-getting-your-order-icon-available')?.innerHTML.trim() ?? '',
    unavailable: block.querySelector('.js-getting-your-order-icon-unavailable')?.innerHTML.trim() ?? '',
    uberDelivery: block.querySelector('.js-getting-your-order-icon-uber-delivery')?.innerHTML.trim() ?? '',
  };
}

function renderAvailabilityStatus(status, icons, { useDeliveryLabel = false } = {}) {
  if (status === 'in_stock') {
    const label = useDeliveryLabel ? 'Available' : 'In Stock';
    return `<span class="getting-your-order__status getting-your-order__status--available"><span class="getting-your-order__status-icon" aria-hidden="true">${icons.available}</span><span>${label}</span></span>`;
  }

  if (status === 'low_stock') {
    return `<span class="getting-your-order__status getting-your-order__status--low-stock"><span class="getting-your-order__status-icon" aria-hidden="true">${icons.available}</span><span>Low Stock</span></span>`;
  }

  if (status === 'unavailable') {
    return `<span class="getting-your-order__status getting-your-order__status--unavailable"><span class="getting-your-order__status-icon" aria-hidden="true">${icons.unavailable}</span><span>Unavailable</span></span>`;
  }

  return `<span class="getting-your-order__prompt"><span class="getting-your-order__prompt-icon" aria-hidden="true"></span><span>${escapeHtml(status)}</span></span>`;
}

function openPickupAvailabilityDrawer(block, opener) {
  const variantData = parseGettingYourOrderData(block);
  const sectionId = block.dataset.sectionId;

  if (!variantData?.variantId || !sectionId) return;

  const drawer = document.getElementById(
    `pickup-availability-${variantData.variantId}-${sectionId}`
  );

  drawer?.open(opener);
}

function renderDesktopSelectors(shop, delivery) {
  const shopLabel = shop?.name ?? 'Select';
  const deliveryLabel = delivery?.postcode ?? delivery?.query ?? 'Select';

  return `
    <div class="getting-your-order__selectors getting-your-order__selectors--desktop">
      <button type="button" class="getting-your-order__selector js-getting-your-order-shop-trigger" data-getting-your-order-trigger>
        ${SHOP_ICON}
        <span class="getting-your-order__selector-label">Shop at</span>
        <span class="getting-your-order__selector-value">${escapeHtml(shopLabel)}</span>
        ${CHEVRON_ICON}
      </button>
      <button type="button" class="getting-your-order__selector js-getting-your-order-delivery-trigger" data-getting-your-order-trigger>
        ${DELIVERY_ICON}
        <span class="getting-your-order__selector-label">Delivery to</span>
        <span class="getting-your-order__selector-value">${escapeHtml(deliveryLabel)}</span>
        ${CHEVRON_ICON}
      </button>
    </div>
  `;
}

function renderDesktopRow({ title, price, statusHtml, detailHtml, extraHtml = '' }) {
  return `
    <div class="getting-your-order__row">
      <div class="getting-your-order__row-header">
        <p class="getting-your-order__row-title">${escapeHtml(title)}</p>
        ${price ? `<p class="getting-your-order__row-price">${escapeHtml(price)}</p>` : ''}
      </div>
      <div class="getting-your-order__row-body">
        ${statusHtml}
        ${detailHtml ? `<span class="getting-your-order__row-detail">${detailHtml}</span>` : ''}
        ${extraHtml}
      </div>
    </div>
  `;
}

function buildStoreFulfilmentStatus(storeSelected, storeQuantity, tags, icons) {
  if (!storeSelected) {
    return renderAvailabilityStatus('Select a store for availability', icons);
  }

  if (!isStoreFulfilmentEligible(storeQuantity, tags)) {
    return renderAvailabilityStatus('unavailable', icons);
  }

  return renderAvailabilityStatus(getAvailabilityStatus(storeQuantity), icons);
}

function buildInStoreStatus(storeSelected, storeQuantity, icons) {
  if (!storeSelected) {
    return renderAvailabilityStatus('Select a store for availability', icons);
  }

  if (!isInStoreEligible(storeQuantity)) {
    return renderAvailabilityStatus('unavailable', icons);
  }

  return renderAvailabilityStatus(getAvailabilityStatus(storeQuantity), icons);
}

function buildFulfillmentState(variantData, shop, delivery) {
  const tags = variantData?.tags ?? [];
  const productType = variantData?.productType ?? '';
  const pharmacistOnly = isPharmacistOnlyProduct(productType);
  const onlineStockStatus = variantData?.onlineStockStatus ?? 'pending';
  const onlineQuantity =
    onlineStockStatus === 'ready' ? Number(variantData?.onlineQuantity) || 0 : null;
  const storeAvailabilityMap = buildStoreAvailabilityMap(variantData?.storeAvailabilities ?? []);
  const storeQuantity = shop
    ? getStoreQuantityAtLocation(storeAvailabilityMap, shop.id ?? shop.locationId)
    : 0;
  const storeSelected = Boolean(shop);
  const deliverySelected = Boolean(delivery);
  const storeFulfilmentEligible = storeSelected && isStoreFulfilmentEligible(storeQuantity, tags);
  const inStoreEligible = storeSelected && isInStoreEligible(storeQuantity);
  const standardDeliveryEligible =
    onlineStockStatus === 'ready' &&
    deliverySelected &&
    isStandardDeliveryEligible(onlineQuantity, tags);
  const sameDayEligible =
    !pharmacistOnly && deliverySelected && storeSelected && storeFulfilmentEligible;

  return {
    tags,
    productType,
    pharmacistOnly,
    onlineQuantity,
    onlineStockStatus,
    storeQuantity,
    storeFulfilmentEligible,
    inStoreEligible,
    standardDeliveryEligible,
    sameDayEligible,
    storeSelected,
    deliverySelected,
    standardStatus:
      onlineStockStatus === 'ready' ? getAvailabilityStatus(onlineQuantity) : null,
    storeStatus: storeSelected ? getAvailabilityStatus(storeQuantity) : null,
  };
}

function renderGettingYourOrderContent(block, variantData) {
  const settings = getBlockSettings(block);
  const resolvedVariantData = variantData ?? parseGettingYourOrderData(block);
  const shop = getShopLocation();
  const delivery = getDeliveryLocation();
  const state = buildFulfillmentState(resolvedVariantData, shop, delivery);
  const statusIcons = getBlockIcons(block);

  const clickCollectStatus = buildStoreFulfilmentStatus(
    state.storeSelected,
    state.storeQuantity,
    state.tags,
    statusIcons
  );
  const inStoreStatus = buildInStoreStatus(
    state.storeSelected,
    state.storeQuantity,
    statusIcons
  );
  const hasPickupLocations = (resolvedVariantData?.storeAvailabilities?.length ?? 0) > 0;
  const storeInfoLink = state.storeSelected && hasPickupLocations
    ? '<button type="button" class="getting-your-order__store-info link js-getting-your-order-store-info">See store info & check stock nearby</button>'
    : '';

  const standardStatus = !state.deliverySelected
    ? renderAvailabilityStatus('Enter postcode or suburb for availability', statusIcons)
    : state.onlineStockStatus === 'pending'
      ? renderAvailabilityStatus('Checking availability...', statusIcons)
      : !state.standardDeliveryEligible
        ? renderAvailabilityStatus('unavailable', statusIcons)
        : renderAvailabilityStatus(state.standardStatus, statusIcons, { useDeliveryLabel: true });

  const sameDayStatus = state.pharmacistOnly
    ? renderAvailabilityStatus('unavailable', statusIcons)
    : !state.deliverySelected
      ? renderAvailabilityStatus('Enter postcode or suburb for availability', statusIcons)
      : !state.storeSelected
        ? renderAvailabilityStatus('Select a store for availability', statusIcons)
        : !state.sameDayEligible
          ? renderAvailabilityStatus('unavailable', statusIcons)
          : renderAvailabilityStatus(state.storeStatus, statusIcons, { useDeliveryLabel: true });

  const sameDayBadge = state.sameDayEligible
    ? `<span class="getting-your-order__uber-badge" aria-label="Delivered with Uber">${statusIcons.uberDelivery}</span>`
    : '';

  const standardDetailHtml = state.standardDeliveryEligible
    ? buildStandardDeliveryDetailHtml(settings)
    : '';

  const clickCollectDetailHtml = state.storeFulfilmentEligible
    ? escapeHtml(settings.clickCollectTime)
    : '';

  const sameDayDetailHtml = state.sameDayEligible
    ? `${sameDayBadge}${sameDayBadge ? '<br>' : ''}${escapeHtml(settings.sameDayDetail)}`
    : '';

  const fulfillmentList = `
    <div class="getting-your-order__list getting-your-order__list--desktop">
      ${renderDesktopRow({
        title: 'Click & Collect',
        price: 'Free',
        statusHtml: clickCollectStatus,
        detailHtml: clickCollectDetailHtml,
      })}
      ${renderDesktopRow({
        title: 'In Store',
        price: '',
        statusHtml: inStoreStatus,
        detailHtml: '',
        extraHtml: storeInfoLink,
      })}
      ${renderDesktopRow({
        title: 'Standard Delivery',
        price: '',
        statusHtml: standardStatus,
        detailHtml: standardDetailHtml,
      })}
      ${renderDesktopRow({
        title: 'Same Day Delivery',
        price: '',
        statusHtml: sameDayStatus,
        detailHtml: sameDayDetailHtml,
      })}
    </div>
  `;

  return `
    ${renderDesktopSelectors(shop, delivery)}
    ${fulfillmentList}
  `;
}

function bindStoreInfoTrigger(block) {
  block.querySelector('.js-getting-your-order-store-info')?.addEventListener('click', (event) => {
    event.preventDefault();
    openPickupAvailabilityDrawer(block, event.currentTarget);
  });
}

export function renderGettingYourOrder(block, variantData = null) {
  const content = block.querySelector('.js-getting-your-order-content');
  if (!content) return;

  content.innerHTML = renderGettingYourOrderContent(block, variantData);
  bindStoreInfoTrigger(block);
}

export function initGettingYourOrderBlock(block) {
  renderGettingYourOrder(block);
}
